import React, { useEffect, useState } from 'react';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  parsePartialSuccessMessage,
  subscribeToPartialSuccess,
} from '../../services/partialSuccessNotice';

/**
 * shows the follow-up the API asked for after a partially successful request. The link stays
 * on screen until it is dismissed and can be copied in one click, since the user has to send
 * it to the candidate by hand.
 * @function PartialSuccessNotice
 * @returns notice dialog
 */
export default function PartialSuccessNotice() {
  const [notices, setNotices] = useState([]);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    return subscribeToPartialSuccess((notice) =>
      setNotices((current) =>
        current.some((item) => item.message === notice.message) ? current : [...current, notice]
      )
    );
  }, []);

  const [notice] = notices;

  useEffect(() => {
    setIsCopied(false);
  }, [notice?.message]);

  if (!notice) return null;

  const { text, url } = parsePartialSuccessMessage(notice.message);

  const handleClose = () => setNotices((current) => current.slice(1));

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setIsCopied(true);
    } catch {
      // clipboard access can be denied - the field is selectable so the link stays copyable
      document.getElementById('partial-success-link')?.select();
    }
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle data-testid="partial-success-notice">Action needed</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: url ? 2 : 0 }}>
          {text}
        </Typography>
        {url && (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              id="partial-success-link"
              value={url}
              size="small"
              fullWidth
              inputProps={{ readOnly: true, 'data-testid': 'partial-success-link' }}
              onFocus={(event) => event.target.select()}
            />
            <Button
              variant="outlined"
              onClick={handleCopy}
              startIcon={isCopied ? <DoneIcon /> : <ContentCopyOutlinedIcon />}
              sx={{ flexShrink: 0 }}
            >
              {isCopied ? 'Copied' : 'Copy'}
            </Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleClose}>
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  );
}
